import { defaultInstance, directApiInstance } from './httpClient';
import type { Estate, EstateGroup } from './generated';

// Property Admin Authorized Estate interface (based on user_estate structure)
export interface PropertyAdminAuthorizedEstate {
  id: number;
  user_id: number; // property_admin_id
  estate_id: number;
  status_id: number; // 2 = approved
  user_type_id: number; // 5 = property admin
  expiration_date: string; // authorized_until
  created_at: string;
  vacay_id: number | null;
  estate?: Estate;
  estate_group?: EstateGroup; // This will come from estate.estate_group
}

// Property Admin Pending Request interface (based on user_estate structure)
export interface PropertyAdminPendingRequest {
  id: number;
  user_id: number; // property_admin_id
  estate_id: number;
  status_id: number; // 1 = pending
  user_type_id: number; // 5 = property admin
  expiration_date: string | null; // null for pending requests
  created_at: string; // requested_at
  vacay_id: number | null;
  requested_duration?: string; // Additional field for property admin requests
  documents?: string[]; // Additional field for property admin requests
  estate?: Estate;
  estate_group?: EstateGroup; // This will come from estate.estate_group
}

// New Access Request interface
export interface NewPropertyAdminAccessRequest {
  estate_id: number;
  estate_group_id: number;
  property_admin_id: number;
  requested_duration: string;
  documents: string[]; // Array of document URLs
}

// Response types
export interface GetPropertyAdminAuthorizedEstates200 {
  data: PropertyAdminAuthorizedEstate[];
  total_pages: number;
  page: number;
  total_elements: number;
}

export interface GetPropertyAdminPendingRequests200 {
  data: PropertyAdminPendingRequest[];
  total_pages: number;
  page: number;
  total_elements: number;
}

export interface PostPropertyAdminAccessRequest200 {
  data: PropertyAdminPendingRequest;
  message: string;
}

// Params types
export interface GetPropertyAdminAuthorizedEstatesParams {
  page?: number;
  limit?: number;
  expand?: string;
  order_by?: string;
  order_direction?: 'ASC' | 'DESC';
  user_id?: number;
  estate_id?: number;
  estate_group_id?: number;
}

export interface GetPropertyAdminPendingRequestsParams {
  page?: number;
  limit?: number;
  expand?: string;
  order_by?: string;
  order_direction?: 'ASC' | 'DESC';
  user_id?: number;
  estate_id?: number;
  estate_group_id?: number;
  status?: 'pending' | 'approved' | 'rejected';
}

/**
 * The Property Manager user_type id of the tenant, found by `user_type.kind = 'property_manager'` (SAM-1218):
 * the ids differ per tenant (Cap Cana: 4, where 5 is Companion), the kind does not. Cached per page load; a
 * failed or empty lookup is not cached and rejects, so no query runs with a wrong type.
 */
let propertyManagerTypeId: Promise<number> | null = null;
export const getPropertyManagerUserTypeId = (): Promise<number> => {
  if (!propertyManagerTypeId) {
    propertyManagerTypeId = defaultInstance<{ data: { id: number }[] }>({
      url: '/user_type',
      method: 'GET',
      params: { kind: 'property_manager', limit: 1 },
    }).then((response) => {
      const rows = (response as unknown as { data?: { id: number }[] }).data;
      const id = Array.isArray(rows) ? rows[0]?.id : undefined;
      if (id == null) throw new Error('Property Manager user type not found');
      return Number(id);
    }).catch((error) => {
      propertyManagerTypeId = null;
      throw error;
    });
  }
  return propertyManagerTypeId;
};

/** `user_estate` has no estate_group_id column (crud: «Unknown field»); the estate comes with its own columns. */
const USER_ESTATE_EXPAND = 'estate,estate_id';

/**
 * Get authorized estates for property admin
 * This fetches user_estate records with approved status for property admins
 */
export const getPropertyAdminAuthorizedEstates = async (
  params?: GetPropertyAdminAuthorizedEstatesParams,
  signal?: AbortSignal
) => {
  const userTypeId = await getPropertyManagerUserTypeId();
  return defaultInstance<GetPropertyAdminAuthorizedEstates200>({
    url: '/user_estate',
    method: 'GET',
    params: {
      ...params,
      user_type_id: userTypeId,
      status_id: 2, // 2 = approved status
      expand: USER_ESTATE_EXPAND,
    },
    signal
  });
};

/**
 * Get pending requests for property admin
 * This fetches user_estate records with pending status for property admins
 */
export const getPropertyAdminPendingRequests = async (
  params?: GetPropertyAdminPendingRequestsParams,
  signal?: AbortSignal
) => {
  const userTypeId = await getPropertyManagerUserTypeId();
  return defaultInstance<GetPropertyAdminPendingRequests200>({
    url: '/user_estate',
    method: 'GET',
    params: {
      ...params,
      user_type_id: userTypeId,
      status_id: 1, // 1 = pending status
      expand: USER_ESTATE_EXPAND,
    },
    signal
  });
};

/**
 * Submit new access request for property admin
 * This creates a user_estate record with pending status
 */
export const postPropertyAdminAccessRequest = async (
  data: NewPropertyAdminAccessRequest,
  signal?: AbortSignal
) => {
  const userTypeId = await getPropertyManagerUserTypeId();
  return directApiInstance<PostPropertyAdminAccessRequest200>({
    url: '/user_estate',
    method: 'POST',
    data: {
      user_id: data.property_admin_id,
      estate_id: data.estate_id,
      user_type_id: userTypeId,
    },
    signal
  });
};

/**
 * Update property admin authorized estate (for extending access)
 * This updates the user_estate record with new expiration date
 */
export const putPropertyAdminAuthorizedEstate = (
  id: string,
  data: Partial<PropertyAdminAuthorizedEstate>,
  signal?: AbortSignal
) => {
  // Find the user_estate record by ID and update it
  return defaultInstance<{ data: PropertyAdminAuthorizedEstate; message: string }>({
    url: `/user_estate/${id}`,
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    data: {
      expiration_date: data.expiration_date,
      // Add any other fields that need updating
    },
    signal
  });
};

/**
 * Delete property admin authorized estate (remove access)
 * This updates the user_estate record to revoked status
 */
export const deletePropertyAdminAuthorizedEstate = (
  id: string,
  signal?: AbortSignal
) => {
  return defaultInstance<{ message: string }>({
    url: `/user_estate/${id}`,
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    data: {
      status_id: 3, // 3 = revoked status
      expiration_date: null,
    },
    signal
  });
}; 